import { useState } from 'react';
import styled, { css } from 'styled-components';
import { backgroundStripes, rem } from 'modules/helpers/style';
import { getTrackingClass } from 'modules/tracking/clickTracking';
import { breakpoint, colors, fonts, sizes } from 'variables';
import Card from 'modules/shared/card';
import type { AdvisorBlockViewModel } from 'autogen/swagger/all';
import ReadMoreMaxCharacters from 'components/react-readmore/readMoreMaxCharacters';
import { SvgSprite } from 'modules/svgSprite';
import ReactButton, { ButtonStyle } from 'modules/react-button';
import { Spacing16, Spacing8 } from 'autogen/design-tokens/tokens';
import Expertises from './components/expertises';
import AdvisorImage from './components/advisorImage';
import LinkAction from 'components/shared/Routing/components/LinkAction';
import { tint } from 'helpers/color';

const Wrapper = styled(Card)`
    position: relative;
    max-width: 100%;
    margin-top: 40px;

    ${breakpoint.up(sizes.md)} {
        margin-top: 0;
    }
`;

const Lines = styled.div`
    ${backgroundStripes({ width: '15px', color1: tint(0.9, colors.color24) })}
    width: 100%;
    height: 30px;
`;

const Content = styled.div<{ hasImage: boolean }>`
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 100%;
    padding: 20px;
    max-width: 100%;

    ${breakpoint.up(sizes.md)} {
        grid-template-columns: 50% 50%;
        align-items: center;
    }

    ${({ hasImage }) =>
        hasImage &&
        css`
            padding-top: 40px;

            ${breakpoint.up(sizes.md)} {
                padding-top: 10px;
            }
        `};
`;

const AdvisorDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 30px;

    &,
    &:hover {
        text-decoration: none;
    }

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
        order: 0;
    }
`;

const AdvisorImageElement = styled(AdvisorImage)`
    position: absolute;
    top: -40px;
    margin: 0 auto;

    ${breakpoint.up(sizes.md)} {
        position: static;
        margin: 0 40px 0 0;
    }
`;

const nameStyling = css`
    font-weight: bold;
    font-size: ${rem(24)};
    color: ${colors.positive};
    max-width: 100%;
`;

const AdvisorName = styled.div`
    ${nameStyling}
`;

const AdvisorNameLink = styled(LinkAction)`
    ${nameStyling}
`;

const AdvisorTitle = styled.div`
    max-width: 100%;
    font-size: ${rem(fonts.sizeLarge)};

    a &,
    a &:hover {
        text-decoration: underline;
    }
`;

const Description = styled.div`
    margin: 0 0 30px;
    max-width: 100%;

    ${breakpoint.up(sizes.md)} {
        grid-column: 1 / 3;
        order: 2;
    }
`;

const StoreLink = styled(LinkAction)`
    display: flex;
    align-items: flex-end;
    text-decoration: underline;
    margin: 20px 0 40px;
`;

const QuotationMark = styled.div`
    font-weight: bold;
    font-size: 40px;
    line-height: 1;
    text-align: center;
`;

const PersonalText = styled.div`
    font-weight: bold;
    font-size: ${fonts.sizeLarge};
    text-align: center;
    max-width: 85%;
    margin: 0 auto 20px;

    ${breakpoint.up(sizes.md)} {
        max-width: 70%;
    }
`;

const PinIcon = styled(SvgSprite)`
    width: 20px;
    height: 20px;
`;

const ReadMoreButton = styled(ReactButton)`
    padding: 0;
    margin-top: ${Spacing16};
`;

const StyledReadMoreMaxCharacters = styled(ReadMoreMaxCharacters)`
    margin-top: ${Spacing8};
`;

export interface IAdvisorContextPlaceholderProps {
    model: AdvisorBlockViewModel;
}

export const AdvisorContextPlaceholder = (props: IAdvisorContextPlaceholderProps): JSX.Element => {
    const { model } = props;
    const {
        name,
        title,
        showPersonalText,
        description,
        personalText,
        profileImages,
        webPageLink,
        expertises,
        storeDetails,
        readMoreText
    } = model;

    const [expandInfo, setExpandInfo] = useState(false);

    return (
        <Wrapper>
            <Lines />
            <Content hasImage={!!profileImages[0]}>
                <AdvisorDetails>
                    {!!profileImages.length && <AdvisorImageElement image={profileImages[0]} />}
                    <div>
                        {webPageLink ? (
                            <AdvisorNameLink
                                action={webPageLink}
                                className={getTrackingClass('advisorContextPlaceholder', 'link')}
                            >
                                {name}
                            </AdvisorNameLink>
                        ) : (
                            <AdvisorName>{name}</AdvisorName>
                        )}

                        <AdvisorTitle>{title}</AdvisorTitle>
                    </div>
                </AdvisorDetails>
                {showPersonalText && (
                    <Description>
                        {description && (
                            <div>
                                <StyledReadMoreMaxCharacters maxLength={320} expand={expandInfo}>
                                    {description}
                                </StyledReadMoreMaxCharacters>
                            </div>
                        )}
                        {expandInfo && storeDetails && (
                            <>
                                <StoreLink action={storeDetails.storeLinkAction}>
                                    <PinIcon sprite="MapsPin" />
                                    {storeDetails.storeName}
                                </StoreLink>
                                <QuotationMark>&ldquo;</QuotationMark>
                                <PersonalText>{personalText}</PersonalText>
                            </>
                        )}
                        {!expandInfo && (
                            <div>
                                <ReadMoreButton buttonStyle={ButtonStyle.link} onClick={() => setExpandInfo(true)}>
                                    {readMoreText}
                                </ReadMoreButton>
                            </div>
                        )}
                    </Description>
                )}
                {!!expertises?.length && <Expertises {...model} />}
            </Content>
        </Wrapper>
    );
};

export default AdvisorContextPlaceholder;
